'use client'

import { ReactNode } from 'react'

import { stegaClean } from 'next-sanity'

import { Link } from '@/app/components/Link'
import { TrackingLocation } from '@/app/utils/analytics'
import { buildDocumentPath } from '@/utils/studio'

// The shared query builds internal post links on the legacy /posts/ route; posts live under the blog now
const LEGACY_POST_PATH = /^\/posts\/([^/?#]+)(.*)$/

interface PortableLinkProps {
  link: any
  children: ReactNode
  tracking: TrackingLocation
  className?: string
}

export default function PortableLink({ link, children, tracking, className }: PortableLinkProps) {
  if (!link) return <>{children}</>

  // Clean stega encoding
  const cleanLink = stegaClean(link)
  const legacyPost = LEGACY_POST_PATH.exec(cleanLink.href ?? '')
  const href = legacyPost
    ? `${buildDocumentPath({ type: 'post', path: legacyPost[1] })}${legacyPost[2]}`
    : cleanLink.href

  if (!href) {
    return <span className={className}>{children}</span>
  }

  // Convert to format expected by Link component
  const linkData = {
    _type: 'link' as const,
    href,
    openInNewTab: cleanLink.openInNewTab ?? null,
    label: typeof children === 'string' ? children : null,
    ariaLabel: null,
    external: null,
    mailto: null,
    tel: null,
    anchor: null,
    linkType: null,
    internalPage: null,
    internalPost: null,
  }

  return (
    <Link link={linkData} tracking={tracking} className={className}>
      {children}
    </Link>
  )
}
